import React from 'react';

import { useAuthGuard } from './useAuthGuard';
import type { IErrorResult } from '../utils/types';
import type { IAffProjectResponse } from '../types/affiliates.types';
import CloudpassService from '../services/cloudpassService';
import { errorHandling } from '../utils/errors';
import { ensureProtocol } from '../utils/netUtils';
import { apiActionError } from '../utils/apiError';

interface ProjectConfigurationContextType {
  data: IAffProjectResponse | null;
  error: IErrorResult | null;
  loading: boolean;
  redirectToProductRegister: (productCP: string) => Promise<null>;
  reactivateProduct: (cp: string, bindingId: string) => Promise<void>;
  getHint: (cp: string) => Promise<void>;
  getRecoveryAccountUrl: (cp: string) => Promise<void>;
}

const ProjectConfigurationContext = React.createContext<ProjectConfigurationContextType | undefined>(undefined);

/**
 * Comparte una única instancia de la data de configuración/subscripciones del
 * proyecto entre todos los componentes descendientes de ProjectConfigurationProvider,
 * evitando que cada consumidor dispare su propio fetch (ver AF-999).
 */
function useProjectConfiguration() {
  const ctx = React.useContext(ProjectConfigurationContext);

  if (!ctx) {
    throw new Error('You need wrap your app with ProjectConfigurationProvider');
  }

  return [
    ctx.data,
    ctx.error,
    ctx.loading,
    ctx.redirectToProductRegister,
    ctx.reactivateProduct,
    ctx.getHint,
    ctx.getRecoveryAccountUrl
  ] as const;
}

export function ProjectConfigurationProvider({ children }: { children: React.ReactNode }) {
  const [data, setData] = React.useState<IAffProjectResponse | null>(null);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<IErrorResult | null>(null);
  const { affServiceInstance, configState, cpassServiceInstance} = useAuthGuard();

  async function redirectToProductRegister(productCP: string) {
    const jwtSession = localStorage.getItem('jwtSession');
    try {
      const redirectTo = await affServiceInstance?.getActivateProductURL(
        configState?.deviceToken!,
        jwtSession!,
        productCP
      );
      window.open(redirectTo!, "_blank");
      return null;
    } catch (e) {
      await errorHandling(e, cpassServiceInstance!, affServiceInstance!, configState, setError);
      return null;
    }
  }

  const getJwtSession = React.useCallback(async (sessionCode: string) => {
    const result = await affServiceInstance?.getJwtSession(sessionCode);
    if (result.error) {
      throw new Error(JSON.stringify(result.error));
    }
    localStorage.setItem('jwtSession', result);

    // clear url session_code param
    const url = new URL(window.location.href);
    url.searchParams.delete('session_code');
    window.history.replaceState({}, document.title, url.toString());

    return result;
  }, [affServiceInstance]);


  const checkJwtSession = React.useCallback(async (jwtSession: string, deviceId: string) => {
    try {
      const result = await affServiceInstance?.checkJwtSession(jwtSession, deviceId);
      return result;
    } catch (error) {
      throw error;
    }
  }, [affServiceInstance]);

  const _fetchProjectData = React.useMemo(() => async () => {
    setError(null);
    setLoading(true);
    setData(null);

    try {

      const validationStatus = await evaluateSession();

      if (!validationStatus.status) {
        if (window !== undefined) {
          await logoutAndRedirect();
          return;
        }
        return;
      }

      // NEW FLOW
      let jwtSession = localStorage.getItem('jwtSession');

      // OLD FLOW - TO BE REMOVED
      const hasParamToolboxUserToken = window.location.search.includes('toolbox_user_token');

      // renew jwt_session if session_code param is present
      if (!hasParamToolboxUserToken && configState?.sessionCode) {
        jwtSession = await getJwtSession(configState?.sessionCode);
      }

      // check actual jwt session
      if (jwtSession && !hasParamToolboxUserToken) {
        await checkJwtSession(jwtSession, validationStatus?.user_token);
      }

      if (!jwtSession && !hasParamToolboxUserToken && !configState?.sessionCode) {
        await logoutAndRedirect();
        return;
      }

      const result = await affServiceInstance?.getProjectConfiguration(
        hasParamToolboxUserToken ? validationStatus?.user_token! : null,
        !hasParamToolboxUserToken && jwtSession ? jwtSession! : undefined,
        configState?.cpfiltered!
      );

      if (result instanceof Error || result?.message || result?.stack) {
        throw result;
      }

      if (result.error) {
        throw new Error(JSON.stringify(result.error));
      }

      setData(result);
    } catch(e) {
      console.error(e);
      await errorHandling(e, cpassServiceInstance!, affServiceInstance!, configState, setError);
    }
    setLoading(false);
  }, [affServiceInstance, configState?.cpfiltered, configState?.deviceToken, cpassServiceInstance]);


  const logoutAndRedirect = React.useCallback(async () => {
    localStorage.removeItem('jwtSession');
    await cpassServiceInstance?.logout();
    const oauthURL = cpassServiceInstance?.getOauth2LoginURL({
      countryCode: configState!.countryCode!,
      idpShortName: configState!.idp!,
      redirectURL: affServiceInstance?.getBindingInitializationURL()!
    });
    window.location.replace(oauthURL!);
  }, [cpassServiceInstance, configState, affServiceInstance]);

  async function evaluateSession() {
    try {
      const instance = new CloudpassService(configState?.cp!);
      const result = await instance.getTADLoginStatus();
      if (result?.error) {
        throw new Error(JSON.stringify(result.error));
      }
      return result;
    } catch (e) {
      throw e;
    }
  }

  async function reactivateProduct(cp: string, bindingId: string) {
    setError(null);
    setLoading(true);

    try {
      const result = await affServiceInstance?.postReactivateProduct(cp, {
        binding: {
          action: 'ACCOMPLISH',
          data: {
            bindingId,
          },
        },
      });
      if (result.error) {
        throw new Error(JSON.stringify(result.error));
      }
      setData(null);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }

  async function getHint(cp: string) {
    try {
      const jwtSession = localStorage.getItem('jwtSession');
      const result = await affServiceInstance?.getHint(
        cp,
        configState?.deviceToken!,
        jwtSession!
      );

      if (result?.error) {
        throw apiActionError(result.error);
      }

      if (!result?.url) {
        throw new Error('La respuesta del hint no incluye url');
      }

      const urlWithProtocol = ensureProtocol(result.url);
      window.open(urlWithProtocol, "_blank");
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  async function getRecoveryAccountUrl(cp: string) {
    setLoading(true);
    try {
      const jwtSession = localStorage.getItem('jwtSession');
      const result = await affServiceInstance?.getRecoveryAccountUrl(
        cp,
        configState?.deviceToken!,
        jwtSession!
      );

      if (result?.error) {
        throw apiActionError(result.error);
      }

      const urlWithProtocol = ensureProtocol(result);
      window.open(urlWithProtocol, "_blank");
    } catch (e) {
      console.error(e);
      throw e;
    } finally {
      setLoading(false);
    }
  }

  React.useEffect(() => {
    if (!data) {
      _fetchProjectData();
    }
  }, [affServiceInstance, configState?.deviceToken, data, _fetchProjectData]);

  const context = {
    data,
    error,
    loading,
    redirectToProductRegister,
    reactivateProduct,
    getHint,
    getRecoveryAccountUrl
  };

  return (
    <ProjectConfigurationContext.Provider value={context}>
      {children}
    </ProjectConfigurationContext.Provider>
  );
}

export default useProjectConfiguration;